import type { LayerSpec } from '@/core/pipeline';
import { buildWorley, worleyDist } from '@/lib/noise';
import { lerpRGB, parseHex, type RGB } from '@/lib/color';

type WorleyParams = {
  sites: number;
  mode: 'f1' | 'f2-f1' | 'f2';
  invert: boolean;
  a: string;
  b: string;
};

export const worleySpec: LayerSpec = {
  kind: 'worley',
  category: 'generator',
  animated: false,
  label: 'Worley Noise',
  defaults: {
    sites: 24,
    mode: 'f1',
    invert: false,
    a: '#000000',
    b: '#ffffff',
  } satisfies WorleyParams,
  schema: {
    sites:  { type: 'int',    label: 'Sites',  min: 2,   max: 256, step: 1, default: 24 },
    mode:   { type: 'select', label: 'Mode',   options: ['f1', 'f2-f1', 'f2'], default: 'f1' },
    invert: { type: 'bool',   label: 'Invert', default: false },
    a:      { type: 'color',  label: 'Low',    default: '#000000' },
    b:      { type: 'color',  label: 'High',   default: '#ffffff' },
  },
  run: (_input, params, ctx) => {
    const p = params as WorleyParams;
    const colA = parseHex(p.a) ?? ([0, 0, 0] as RGB);
    const colB = parseHex(p.b) ?? ([255, 255, 255] as RGB);
    const w = ctx.width, h = ctx.height;
    const out = new ImageData(w, h);
    const data = out.data;
    const count = Math.max(2, Math.floor(p.sites));
    const sites = buildWorley(ctx.seed, count, w, h);
    const norm = Math.max(1, Math.sqrt(w * h) / Math.sqrt(count));

    let i = 0;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++, i += 4) {
        const r = worleyDist(sites, x, y);
        let v: number;
        if (p.mode === 'f2-f1') v = (r.f2 - r.f1) / norm;
        else if (p.mode === 'f2') v = r.f2 / (norm * 2);
        else v = r.f1 / norm;
        let t = clamp01(v);
        if (p.invert) t = 1 - t;
        const c = lerpRGB(colA, colB, t);
        data[i] = c[0]; data[i+1] = c[1]; data[i+2] = c[2]; data[i+3] = 255;
      }
    }
    return out;
  },
};

function clamp01(n: number): number {
  return n < 0 ? 0 : n > 1 ? 1 : n;
}
